// The Products tab: which products to push, and a panel for each.
import { useEffect, useMemo, useState } from "react";
import { getProductOpportunities } from "../../distflowAPI/salesAnalyticsApi";
import Answer from "./Answer";
import ProductPanel from "./ProductPanel";
import { productsAnswer } from "./answers";
import SplitView from "./SplitView";
import { count, money, pct, signedPct } from "./format";
import useSideReport from "./useSideReport";
import "./ProductOpportunities.css";

const PAGE = 25;
const EMPTY = [];
const num = (value) => Number(value || 0);

const VIEWS = [
  { key: "opportunity", label: "Could sell more", hint: "Selling well to some customers of a type but missing from many similar ones. Ranked by the gross profit each could add a week.", sort: null },
  { key: "rising", label: "Rising", needsComparison: true, hint: "Gaining buyers since the comparison dates.",
    sort: (a, b) => (b.customers_gained - b.customers_lost) - (a.customers_gained - a.customers_lost) || num(b.sales_change_amount) - num(a.sales_change_amount) },
  { key: "falling", label: "Falling", needsComparison: true, hint: "Losing buyers, or sales down 25% or more, since the comparison dates.",
    sort: (a, b) => (b.customers_lost - b.customers_gained) - (a.customers_lost - a.customers_gained) || num(a.sales_change_amount) - num(b.sales_change_amount) },
  { key: "not_reordered", label: "Not reordered", hint: "Five or more customers bought it but under 30% bought it again on another day. Worth asking why before pushing it.",
    sort: (a, b) => num(a.reorder_rate_percent) - num(b.reorder_rate_percent) },
  { key: "low_margin", label: "Low margin", hint: "Gross margin under 10%: selling more of these adds little profit.", sort: (a, b) => num(a.margin_percent) - num(b.margin_percent) },
  { key: "all", label: "All products", hint: "Every product sold in these dates, by sales.", sort: (a, b) => num(b.sales) - num(a.sales) },
];

function Momentum({ row }) {
  if (row.customers_gained === undefined) return <span className="sa-muted">—</span>;
  const net = row.customers_gained - row.customers_lost;
  return <><span className={`sa-strong ${net >= 3 ? "sa-good" : net <= -3 ? "sa-bad" : ""}`}>{net > 0 ? "+" : net < 0 ? "−" : "±"}{Math.abs(net)} buyers</span>
    <small>{row.customers_gained} new, {row.customers_lost} lost{row.sales_change_percent != null ? `, sales ${signedPct(row.sales_change_percent)}` : ""}</small></>;
}

export default function ProductOpportunities({ request, filters = EMPTY, openKey, onOpen, onClose, onOpenCustomer, onFocus }) {
  const [view, setView] = useState("opportunity");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const [attempt, setAttempt] = useState(0);
  const params = useMemo(() => ({ ...request, filters }), [request, filters]);
  const products = useSideReport(getProductOpportunities, params, Boolean(request?.start_date), attempt);
  const data = products.data;
  const hasComparison = Boolean(data?.comparison);
  const activeView = VIEWS.find((item) => item.key === view && (!item.needsComparison || hasComparison)) || VIEWS[0];
  useEffect(() => { setVisible(PAGE); }, [view, category, query, data]);

  const rows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    const list = (data?.rows || []).filter((row) =>
      (activeView.key === "all" || row.tags?.includes(activeView.key))
      && (category === "all" || String(row.category_id) === category)
      && (!term || `${row.name} ${row.sku || ""} ${row.vendor || ""}`.toLocaleLowerCase().includes(term)));
    return activeView.sort ? [...list].sort(activeView.sort) : list;
  }, [data, activeView, category, query]);
  const summary = data?.summary;
  const tagCount = (key) => (key === "all" ? data?.total : summary?.tags?.[key] || 0);
  const openRow = openKey ? (data?.rows || []).find((row) => row.key === openKey) || null : null;
  const panel = openKey && data ? (mode) => <ProductPanel key={openKey} mode={mode} row={openRow} onClose={onClose}
    onOpenCustomer={onOpenCustomer} onFocus={onFocus} /> : null;

  return <>
    <Answer answer={productsAnswer(data)} loading={products.loading} />
    <SplitView panel={panel}>
    <section className="sa-list product-opps" aria-label="Products to push" aria-busy={products.loading}>
      {products.failed && <p className="sa-note" role="alert">Product opportunities could not be loaded.
        <button type="button" className="sa-link-button" onClick={() => setAttempt(attempt + 1)}>Try again</button></p>}
      {!data && products.loading && <p className="sa-list-empty">Scoring products…</p>}
      {data && <>
        <div className="sa-chips" role="group" aria-label="Product lists">
          {VIEWS.map((item) => {
            const disabled = item.needsComparison && !hasComparison;
            return <button type="button" key={item.key} aria-pressed={activeView.key === item.key} disabled={disabled}
              title={disabled ? "Choose a comparison to see this" : undefined}
              className={activeView.key === item.key ? "is-selected" : ""} onClick={() => setView(item.key)}>
              {item.label}{!disabled && <b>{count(tagCount(item.key))}</b>}
            </button>;
          })}
        </div>
        <p className="sa-note">{activeView.hint}</p>
        <div className="sa-tools">
          <input type="search" aria-label="Search products, codes or vendors" placeholder="Search products, codes or vendors" value={query} onChange={(event) => setQuery(event.target.value)} />
          <label>Category
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="all">All categories</option>
              {(data.categories || []).map((item) => <option key={String(item.id)} value={String(item.id)}>{item.name} ({pct(item.margin_percent, 0)} margin)</option>)}
            </select>
          </label>
        </div>
        {rows.length ? <div className="sa-table-scroll"><table className="sa-list-table" aria-label={activeView.label}>
          <thead><tr>
            <th scope="col" className="sa-col-name">Product</th><th scope="col" className="sa-num">Sales a week</th><th scope="col" className="sa-num">Margin</th>
            <th scope="col" className="sa-num">Customers</th><th scope="col" className="sa-num sa-col-secondary">Bought again</th>
            {hasComparison && <th scope="col" className="sa-col-secondary">Buyers</th>}<th scope="col" className="sa-num">Could add a week</th>
          </tr></thead>
          <tbody>{rows.slice(0, visible).map((row) => <tr key={row.key} className={openKey === row.key ? "is-open" : ""} onClick={() => onOpen?.(row.key)}>
            <th scope="row" className="sa-col-name">
              <button type="button" className="sa-row-link" aria-current={openKey === row.key ? "true" : undefined} aria-controls="sa-detail-panel"
                onClick={(event) => { event.stopPropagation(); onOpen?.(row.key); }}>{row.name}</button>
              <small>{[row.sku, row.category, row.vendor].filter(Boolean).join(", ")}</small>
            </th>
            <td className="sa-num" data-label="Sales a week">{money(row.weekly_sales)}<small>{money(row.sales)} in all</small></td>
            <td className={`sa-num${row.tags?.includes("low_margin") ? " sa-warn" : ""}`} data-label="Margin">{pct(row.margin_percent)}
              <small>{row.gross_profit != null ? `${money(row.gross_profit)} profit` : "no cost"}</small></td>
            <td className="sa-num" data-label="Customers">{count(row.customers)}<small>{pct(row.reach_percent, 0)} of customers</small></td>
            <td className={`sa-num sa-col-secondary${row.tags?.includes("not_reordered") ? " sa-warn" : ""}`} data-label="Bought again">{row.customers >= 3 ? pct(row.reorder_rate_percent, 0) : "—"}</td>
            {hasComparison && <td className="sa-col-secondary" data-label="Buyers"><Momentum row={row} /></td>}
            <td className="sa-num" data-label="Could add a week">{num(row.potential_weekly) > 0
              ? <><span className="sa-strong">{money(row.potential_weekly)}</span><small>{row.potential_profit_weekly != null ? `${money(row.potential_profit_weekly)} profit` : "margin unknown"}, {count(row.target_total)} customers</small></>
              : <span className="sa-muted">—</span>}</td>
          </tr>)}</tbody>
        </table></div> : <p className="sa-list-empty">{data.rows?.length ? "No products in this list." : "No product sales in these dates."}</p>}
        <div className="sa-list-foot">
          <span>{rows.length > visible ? `Showing ${visible} of ${count(rows.length)}.` : rows.length ? `${count(rows.length)} ${rows.length === 1 ? "product" : "products"}.` : ""}
            {activeView.key === "all" && data.total > (data.rows?.length || 0) ? ` The top ${count(data.rows.length)} of ${count(data.total)} are listed.` : ""}</span>
          {rows.length > visible && <button type="button" className="sa-link-button" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more</button>}
        </div>
      </>}
    </section>
  </SplitView>
  </>;
}
